/**
 * React Query hooks for the provable-assurance surface (P154).
 *
 * Mirrors backend/dossier/assurance_router.py:
 *   GET /v1/assurance/questions
 *   GET /v1/assurance/answer/{id}
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { api, type ApiError } from "shared/api/client";
import type { AssuranceAnswer, AssuranceQuestion } from "shared/api/types";

export const assuranceKeys = {
  questions: () => ["assurance", "questions"] as const,
  answer: (id: string) => ["assurance", "answer", id] as const,
};

export function useAssuranceQuestions(): UseQueryResult<AssuranceQuestion[], ApiError> {
  return useQuery({
    queryKey: assuranceKeys.questions(),
    queryFn: () => api.get<AssuranceQuestion[]>("/v1/assurance/questions"),
    retry: false,
  });
}

export function useAssuranceAnswer(
  questionId: string | null,
): UseQueryResult<AssuranceAnswer, ApiError> {
  return useQuery({
    queryKey: assuranceKeys.answer(questionId ?? ""),
    queryFn: () =>
      api.get<AssuranceAnswer>(
        `/v1/assurance/answer/${encodeURIComponent(questionId ?? "")}`,
      ),
    enabled: Boolean(questionId),
    retry: false,
  });
}
